import React, { useCallback, useEffect, useState } from "react";
import { StriimTypography, StriimLink, StriimButton, StriimModal } from "@striim/striim-ui-v2";
import { Box, List, ListItem } from "@mui/material";
import sxClasses from "../flowdesigner.styles";
import metaStoreService from "core/services/metaStoreService/meta-store-service";
import metaobjectConverter from "core/services/metaStoreService/metaobject-converter";
import { useAppSelector } from "src/stores/redux/hooks";
import { getCurrentFlowId, getAppFQN } from "../helpers/selectors";
import { useFetchAndPopulateNodes } from "../helpers/flow.hooks";
import createContinuousApp from "app/components/flow/designer/graphNodes/createContinuousApp";
import growl from "app/components/common/growl";
import api from "core/api/api";

interface FlowEmptyStateProps {
    appStatus: string | null;
}

const FlowEmptyState: React.FC<FlowEmptyStateProps> = ({ appStatus: _appStatus }) => {
    const currentFlowId = useAppSelector(getCurrentFlowId);
    const appFQN = useAppSelector(getAppFQN);
    const appName = metaobjectConverter.getName(appFQN);

    const [isInSubFlow, setIsInSubFlow] = useState(false);
    const [isNewUser, setIsNewUser] = useState(true);
    const [hasPosApp, setHasPosApp] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const fetchAndPopulateNodes = useFetchAndPopulateNodes();

    const isFlow = currentFlowId !== appFQN;

    useEffect(() => {
        let newIsNewUser = true;
        let newHasPosApp = false;
        const newIsInSubFlow = metaobjectConverter.getType(currentFlowId) === metaStoreService.entities.FLOW;
        metaStoreService.fetchIdentifiersByType(metaStoreService.entities.APPLICATION).then(function(apps: any) {
            apps.each(function(app: any) {
                if (app.get("nsName").toLowerCase() === "samples" && app.get("name").toLowerCase() === "posapp") {
                    newHasPosApp = true;
                }
                if (app.get("nsName").toLowerCase() !== "samples" && app.get("id") !== currentFlowId) {
                    newIsNewUser = false;
                }
            });
            setHasPosApp(newHasPosApp);
            setIsNewUser(newIsNewUser);
            setIsInSubFlow(newIsInSubFlow);
        });
    }, [currentFlowId]);

    const showAutoGenDialog = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setIsDialogOpen(true);
    }, []);

    const handleCreateSimpleSource = useCallback(() => {
        setIsDialogOpen(false);
        createContinuousApp
            .createSimpleSource(currentFlowId)
            .then(() => {
                api.compile(appName)
                    .then((result: string) => {
                        growl.success(result, "Compilation Success", appName, appName);
                        fetchAndPopulateNodes();
                    })
                    .catch((e: any) => {
                        growl.error(e.message, "Compilation failed", appName, appName);
                    });
            })
            .fail((e: string) => {
                growl.error(e, "Error", undefined, appName);
            });
    }, [currentFlowId]);

    const handleCreateAdvancedSource = useCallback(() => {
        setIsDialogOpen(false);
        createContinuousApp
            .createAdvancedSource(currentFlowId)
            .then(() => {
                api.compile(appName)
                    .then((result: string) => {
                        growl.success(result, "Compilation Success", appName, appName);
                        fetchAndPopulateNodes();
                    })
                    .catch((e: any) => {
                        growl.error(e.message, "Compilation failed", appName, appName);
                    });
            })
            .fail((e: string) => {
                growl.error(e, "Error", undefined, appName);
            });
    }, [currentFlowId]);

    return (
        <>
            <Box sx={sxClasses.emptyStateWrapper}>
                <Box
                    component="img"
                    src="src/generic/icon/manage-striim/03-Apps-CreateApps.svg"
                    sx={sxClasses.emptyStateSvg}
                />

                {isNewUser ? (
                    <Box textAlign="center">
                        <StriimTypography variant="h2">Welcome! New to Striim?</StriimTypography>

                        {isInSubFlow ? (
                            <StriimTypography variant="body4" color="greyscale.700">
                                Get started by dragging a new component from the sidebar on the left.
                            </StriimTypography>
                        ) : (
                            <Box>
                                {/* Main onboarding text */}
                                <StriimTypography variant="body4" color="greyscale.700">
                                    Get started by adding an auto-generating data source to this application{" "}
                                    {!hasPosApp ? (
                                        <>
                                            by clicking{" "}
                                            <StriimLink
                                                id="addAutoGenSource"
                                                sx={{
                                                    minWidth: "0 !important",
                                                    width: "28px !important",
                                                    height: "auto !important",
                                                    p: "0 !important"
                                                }}
                                                onClick={showAutoGenDialog}
                                            >
                                                here
                                            </StriimLink>
                                        </>
                                    ) : (
                                        <>
                                            or browse the <br />
                                            Point Of Sale sample application
                                        </>
                                    )}
                                </StriimTypography>

                                {/* Buttons when POS app exists */}
                                {hasPosApp && (
                                    <Box mt={2} display="flex" gap={2}>
                                        <StriimButton variant="tertiary" onClick={showAutoGenDialog}>
                                            Add Auto-Generating Source
                                        </StriimButton>

                                        <StriimButton
                                            variant="tertiary"
                                            onClick={() => {
                                                window.location.hash = "#/flow/Samples.PosApp";
                                            }}
                                        >
                                            View Point Of Sale Application
                                        </StriimButton>
                                    </Box>
                                )}

                                {/* OR section */}
                                <Box mt={2}>
                                    <StriimTypography variant="body4" color="greyscale.700">
                                        Or
                                    </StriimTypography>
                                    <br />
                                    <StriimTypography variant="body4" color="greyscale.700">
                                        You can start by dragging a new component from the sidebar on the left.
                                    </StriimTypography>
                                </Box>
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Box textAlign="center">
                        <StriimTypography variant="h2">Your {appName} is empty!</StriimTypography>
                        <br />
                        <StriimTypography variant="body4" color="greyscale.700">
                            You can start by dragging a new component from the sidebar on the left or browse existing
                            components from ‘Metadata Browser’.
                        </StriimTypography>

                        {!isInSubFlow && (
                            <Box mt={2}>
                                <StriimTypography variant="body4" color="greyscale.700">
                                    Or{" "}
                                </StriimTypography>
                                <StriimTypography variant="body4" color="greyscale.700">
                                    Click{" "}
                                    <StriimLink
                                        id="addAutoGenSource"
                                        sx={{
                                            minWidth: "0 !important",
                                            width: "28px !important",
                                            height: "auto !important",
                                            p: "0 !important"
                                        }}
                                        onClick={showAutoGenDialog}
                                    >
                                        here
                                    </StriimLink>{" "}
                                    to add an auto-generating data source to this application.
                                </StriimTypography>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            <StriimModal
                isVisible={isDialogOpen}
                onCancel={() => setIsDialogOpen(false)}
                titleContent={<StriimTypography variant="h2">Select A Source</StriimTypography>}
                cancelContent="Cancel"
                dialogProps={{
                    sx: { ".MuiDialog-paper": { width: "900px", minWidth: "800px", height: "460px !important" } }
                }}
            >
                <Box display="flex" gap={4}>
                    {/* Simple Source */}
                    <Box flex={1}>
                        <StriimTypography variant="h3">Simple Source</StriimTypography>
                        <StriimTypography variant="body4" color="greyscale.700">
                            Create a high throughput source with four fields and random data. This source has the
                            following fields:
                        </StriimTypography>
                        <List sx={{ listStyleType: "disc", pl: 3, height: 200, overflow: "auto", mt: 1 }}>
                            {["Timestamp", "Source IP", "Target IP", "HTTP Code"].map(field => (
                                <ListItem
                                    key={field}
                                    sx={{
                                        display: "list-item",
                                        py: 0.5,
                                        borderBottom: "none",
                                        pointerEvents: "none",
                                        cursor: "default"
                                    }}
                                >
                                    <StriimTypography variant="body4" color="greyscale.700">
                                        {field}
                                    </StriimTypography>
                                </ListItem>
                            ))}
                        </List>
                        <StriimButton
                            data-test-id="create-simple-source-button"
                            variant="primary"
                            onClick={handleCreateSimpleSource}
                        >
                            Create Source
                        </StriimButton>
                    </Box>

                    {/* Advanced Source */}
                    <Box flex={1} borderLeft="1px solid" sx={{ borderColor: "greyscale.200" }} pl={4}>
                        <StriimTypography variant="h3">Advanced Source</StriimTypography>
                        <StriimTypography variant="body4" color="greyscale.700">
                            Create a low throughput source with seven fields and real world data. This source has the
                            following fields:
                        </StriimTypography>
                        <List sx={{ listStyleType: "disc", pl: 3, height: 200, overflow: "auto", mt: 1 }}>
                            {[
                                "Timestamp",
                                "Name",
                                "Username",
                                "Phone number",
                                "Credit Card",
                                "Product Name",
                                "IP Address"
                            ].map(field => (
                                <ListItem
                                    key={field}
                                    sx={{
                                        display: "list-item",
                                        py: 0.5,
                                        borderBottom: "none",
                                        pointerEvents: "none",
                                        cursor: "default"
                                    }}
                                >
                                    <StriimTypography variant="body4" color="greyscale.700">
                                        {field}
                                    </StriimTypography>
                                </ListItem>
                            ))}
                        </List>
                        <StriimButton
                            sx={{ mt: 1 }}
                            data-test-id="create-advanced-source-button"
                            variant="primary"
                            onClick={handleCreateAdvancedSource}
                        >
                            Create Source
                        </StriimButton>
                    </Box>
                </Box>
            </StriimModal>
        </>
    );
};
export default FlowEmptyState;
